import { FAQLink, FAQTopic } from '@components/FAQ';
import { EXTERNAL_LINKS } from '@constants/externalLinks';

import { Sections, Topics } from './constants';

const NETWORK_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.networkCoverage,
    question: "What's the network coverage like where I live?",
    answer: (
      <>
        Find out using our{' '}
        <FAQLink href={EXTERNAL_LINKS.mobileCoverageChecker}>
          coverage checker.
        </FAQLink>
      </>
    ),
  },
  {
    id: Topics.uwSimWorksWithMobile,
    question: 'Will my UW SIM work in my current phone?',
    answer: (
      <>
        Yes, as long as your handset is unlocked or compatible with the EE
        network. The best way to unlock your handset is to get in touch with
        your current network provider. You can also take it to a local mobile
        shop, but they'll usually charge around £15 for this.
      </>
    ),
  },
  {
    id: Topics.phoneWorkAbroad,
    question: 'Will my phone work abroad?',
    answer: (
      <>
        Yes, you'll be able to make phone calls, texts and use data abroad, but
        depending on the plan you choose and where you go, you might have to pay
        some additional charges and your allowances might be different. For more
        info on data usage abroad, see our{' '}
        <FAQLink href={EXTERNAL_LINKS.overseasRoamingArticle}>
          overseas roaming article,
        </FAQLink>{' '}
        and for more info on making phone calls and texts abroad, see the Mobile
        section of our{' '}
        <FAQLink href={EXTERNAL_LINKS.residentialTariffsAndCharges}>
          residential tariffs and charges
        </FAQLink>{' '}
        information.
      </>
    ),
  },
  {
    id: Topics.unlimitedMeaning,
    question: "Does 'unlimited' really mean unlimited?",
    answer: (
      <>
        Data consumption on our Go Unlimited tariff is subject to a personal
        monthly fair usage amount of 500GB.
      </>
    ),
  },
  {
    id: Topics.support,
    question: 'What support do you offer if I have problems?',
    answer: (
      <>
        We have a comprehensive help and support library available online to
        enable you to resolve most problems quickly. If you can't resolve the
        problem on your own, you can call our customer support, who will run
        diagnostics remotely and help you find a solution.
      </>
    ),
  },
  {
    id: Topics.uwTariffsAndTerms,
    question:
      'Where can I read more about UW mobile tariffs and their terms and conditions?',
    answer: (
      <>
        You can find more detail on our{' '}
        <FAQLink href={EXTERNAL_LINKS.residentialTariffsAndCharges}>
          mobile tariffs here.
        </FAQLink>{' '}
        All tariffs are subject to our{' '}
        <FAQLink href={EXTERNAL_LINKS.termsAndConditions}>
          residential terms and conditions,
        </FAQLink>{' '}
        as well as a{' '}
        <FAQLink href={EXTERNAL_LINKS.mobileFairUsePolicy}>
          fair use policy.
        </FAQLink>
      </>
    ),
  },
];

const SWITCHING_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.canKeepNumber,
    question: 'Can I keep my mobile number when I switch to UW?',
    answer: (
      <>
        If you’d like to keep your current mobile number when you switch to UW,
        you’ll need a PAC (Porting Authorisation Code). You can get this by
        texting ‘PAC’ to 65075 for free.
        <br />
        <br />
        All you need to do is share your PAC with us, either when you order your
        SIM or after we’ve sent your new SIM. The codes are valid for 30 days –
        but if your code expires before you use it, you can request another one.
        Once you've shared your PAC with us, your number will switch shortly
        after you activate your new SIM.
      </>
    ),
  },
  {
    id: Topics.contactToCurrentProvider,
    question: 'Do I need to contact my current mobile supplier when I switch?',
    answer: (
      <>
        You won't need to contact your current supplier if you provide us with
        your PAC or STAC and activate your new SIM. When you receive your PAC or
        STAC, you'll also receive all the information you need to switch your
        mobile service, including any fees you might need to pay if you're
        leaving a contract early.
        <br />
        <br />
        If you don't provide a PAC or STAC to us, we can't initiate the
        cancellation of your old contract on your behalf, so then you'll need to
        get in touch with your existing supplier yourself to end your contract.
      </>
    ),
  },
  {
    id: Topics.stuckWithCurrentProvider,
    question:
      "I'm stuck in a mobile contract with another provider. Can I still switch to UW?",
    answer: (
      <>
        You can, but watch out for any fees. To check if your current network
        will charge you for ending your contract early, text “INFO” to 85075
        (it’s free). You’ll get a message back telling you how long is left on
        your contract and if there’s an early termination fee. We refund up to
        £400 if you pay early termination fees when switching your services to
        UW.{' '}
        <FAQLink href={EXTERNAL_LINKS.earlyTerminationTerms}>
          Eligibility and terms apply.
        </FAQLink>
      </>
    ),
  },
  {
    id: Topics.whenSimDelivered,
    question: 'When will my SIM be delivered?',
    answer: <>Typically within 1 to 5 working days.</>,
  },
];

const faqSections = [
  {
    id: Sections.networkAndUsage,
    title: 'Network and usage',
    topics: NETWORK_TOPICS,
  },
  {
    id: Sections.switching,
    title: 'Switching',
    topics: SWITCHING_TOPICS,
  },
];

export { faqSections };
